import React from 'react'
import { motion } from 'framer-motion'

const moodWords = [
  { text: '开心', size: 'text-4xl', color: 'text-yellow-500', emoji: '😊' },
  { text: '放松', size: 'text-3xl', color: 'text-green-500', emoji: '😌' },
  { text: '浪漫', size: 'text-3xl', color: 'text-pink-500', emoji: '💕' },
  { text: '忧郁', size: 'text-2xl', color: 'text-blue-600', emoji: '😔' },
  { text: '兴奋', size: 'text-3xl', color: 'text-orange-500', emoji: '🤩' },
  { text: '平静', size: 'text-2xl', color: 'text-teal-500', emoji: '😇' },
  { text: '思念', size: 'text-2xl', color: 'text-purple-500', emoji: '💭' },
  { text: '愤怒', size: 'text-2xl', color: 'text-red-500', emoji: '😠' },
  { text: '孤独', size: 'text-2xl', color: 'text-gray-600', emoji: '😢' },
  { text: '梦幻', size: 'text-3xl', color: 'text-indigo-500', emoji: '✨' },
  { text: '专注', size: 'text-2xl', color: 'text-blue-700', emoji: '🎯' },
  { text: '怀旧', size: 'text-2xl', color: 'text-amber-600', emoji: '📻' },
  { text: '活力', size: 'text-3xl', color: 'text-lime-500', emoji: '⚡' },
  { text: '温暖', size: 'text-2xl', color: 'text-orange-400', emoji: '☀️' },
  { text: '神秘', size: 'text-2xl', color: 'text-violet-600', emoji: '🌙' },
]

const MoodWordCloud = ({ onMoodSelect, isLoading }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  const wordVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.5,
      y: 20 
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  }

  const getWordPosition = (index) => {
    // 创建更自然的词云布局
    const positions = [
      { x: 0, y: 0 },      // 中心
      { x: -120, y: -60 },  // 左上
      { x: 120, y: -60 },   // 右上
      { x: -200, y: 0 },    // 左中
      { x: 200, y: 0 },     // 右中
      { x: -120, y: 60 },   // 左下
      { x: 120, y: 60 },    // 右下
      { x: 0, y: -120 },    // 上中
      { x: 0, y: 120 },     // 下中
      { x: -80, y: -120 },  // 左上远
      { x: 80, y: -120 },   // 右上远
      { x: -300, y: -30 },  // 左远
      { x: 300, y: -30 },   // 右远
      { x: -80, y: 180 },   // 左下远
      { x: 80, y: 180 },    // 右下远
    ]
    
    return positions[index] || { x: 0, y: 0 }
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="relative w-full h-96 flex items-center justify-center"
    >
      <div className="relative">
        {moodWords.map((word, index) => {
          const position = getWordPosition(index)
          return (
            <motion.button
              key={word.text}
              variants={wordVariants}
              whileHover={{ 
                scale: 1.2,
                rotate: [-1, 1, -1, 0],
                transition: { duration: 0.3 }
              }}
              whileTap={{ scale: 0.9 }}
              onClick={() => !isLoading && onMoodSelect(word.text)}
              disabled={isLoading}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${word.size} ${word.color} font-medium hover:shadow-lg transition-all duration-200 px-3 py-1 rounded-lg hover:bg-white hover:bg-opacity-80 disabled:cursor-not-allowed disabled:opacity-50`}
              style={{
                left: `calc(50% + ${position.x}px)`,
                top: `calc(50% + ${position.y}px)`,
              }}
            >
              <span className="mr-1">{word.emoji}</span>
              {word.text}
            </motion.button>
          )
        })}
        
        {/* 中心提示 */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"
        >
          点击选择心情
        </motion.div>
      </div>
      
      {/* 装饰性元素 */}
      <motion.div
        animate={{ 
          rotate: 360,
          scale: [1, 1.1, 1]
        }}
        transition={{ 
          rotate: { duration: 20, repeat: Infinity, ease: "linear" },
          scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
        }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-4 left-4 w-2 h-2 bg-purple-400 rounded-full opacity-60"></div>
        <div className="absolute top-8 right-8 w-1 h-1 bg-blue-400 rounded-full opacity-40"></div>
        <div className="absolute bottom-8 left-8 w-3 h-3 bg-pink-400 rounded-full opacity-30"></div>
        <div className="absolute bottom-4 right-4 w-1.5 h-1.5 bg-yellow-400 rounded-full opacity-50"></div>
      </motion.div>
    </motion.div>
  )
}

export default MoodWordCloud
